/* @flow */

import { getRandom } from './utils';
import { logError, logDebug } from './utils/logger';
import { extend, intersect } from './utils/coreutils';
import { createIframe, cleanFrame } from './utils/iframeutils';
import { isOptedOut } from './utils/privacyutils';

/**
 * @typedef syncData
 * @property {Array<string>} allowedSyncTypes Usersync Types
 * @property {number} delay Delay for requests
 * @property {number} syncsPerBidder No. of syncs per bidder
 * @property {boolean} enableUserSync Enable/disable usersync
 */

/**
 * @global
 * @typedef syncObj
 * @property {string} name Name
 * @property {string} url URL for usersync request
 * @property {string} type usersync type
 */

declare type syncCB = (HTMLElement, syncObj) => void;
declare type syncProcesser = {
  [key: 'image' | 'iframe']: syncCB
};

/**
 * @module usersync
 * @requires module:logger
 * @requires module:coreutils
 */
/**
 * @description Provides functions to fire user sync pixels/iframes
 */

class _userSync {
  _queue: Array<syncObj>;

  _config: syncData;

  _defConfig: syncData;

  _syncContainerId: string;

  _syncCountPerBidder: { [key: string]: number };

  _processor: syncProcesser;

  constructor() {
    this._queue = [];
    this._defConfig = {
      delay: 1000,
      syncsPerBidder: 5, // number of syncs per bidder
      enableUserSync: false, // global config to disable user sync feature
      allowedSyncTypes: []
    };
    this._syncCountPerBidder = {};
    this._syncContainerId = `sf-${getRandom()}`;
    this._processor = {
      // $FlowFixMe
      image: this._fireImagePixel,
      // $FlowFixMe
      iframe: this._loadIframe
    };
  }

  /**
   * Update user sync module settings from config
   *
   * @param {syncData} settings - config settings for user sync
   * @returns {undefined}
   */
  updateSettings(settings: syncData = {}) {
    this._config = extend(true, {}, this._defConfig, settings);
  }

  /**
   * Collects list of user sync objects from all the ative bidders
   *
   * @external hbPartnerBase
   * @param {Array<hbPartnerBase>} bidders List of bidders
   * @returns {void}
   */
  handleUserSync(bidders: Array<hbPartnerBase>) {
    // check if usersync is enabled
    if (!this.isUserSyncEnabled()) {
      return;
    }

    const syncObjects: Array<syncObj> = bidders
      .flatMap(b => b.getUserSync())
      .filter(
        (e: syncObj) =>
          Object.keys(e).length > 0 &&
          e.type &&
          e.type.length > 0 &&
          e.url &&
          e.url.length
      );
    if (syncObjects.length) {
      this._queueSyncs(syncObjects);
    }
  }

  /**
   * Returns if user sync global feature is enabled
   *
   * @returns {boolean} - if user sync is enabled
   */

  isUserSyncEnabled(): boolean {
    return this._config.enableUserSync && !isOptedOut();
  }

  /**
   * Adds all sync objects to queue bases on the type of sync
   *
   * @param {Array<object>} syncRequests - List of sync objects
   * @returns {undefined}
   * @access private
   */
  _queueSyncs(syncRequests: Array<syncObj>) {
    const supportedUS = Object.keys(this._processor);
    const allowedUS = intersect(supportedUS, this._config.allowedSyncTypes);

    // Add only supported & enabled sync types to the queue
    // Check if sync per bidder count is reached as well
    this._queue = syncRequests
      .filter(e => {
        const typeSupported = allowedUS.includes(e.type);
        if (!typeSupported) {
          logDebug(`UserSync type ${e.type} is not allowed for ${e.name}`);
        }
        return typeSupported;
      })
      .filter(e => {
        const cnt = this._syncCountPerBidder[e.name] || 0;
        const allowed = cnt < this._config.syncsPerBidder;
        if (allowed) {
          this._syncCountPerBidder[e.name] = cnt + 1;
        } else {
          logDebug(`UserSync count reached limit for ${e.name}`);
        }
        return allowed;
      });
    if (this._queue.length) {
      setTimeout(() => this.fireSync(), this._config.delay);
    }
  }

  /**
   * Creates sync container if it does not exists and cleans it
   *
   * @returns {HTMLElement} - Div container for sync frames and pixels
   * @access private
   */
  _createAndCleanContainer(): HTMLElement {
    const doc: Document = window.document;
    let syncDiv = doc.getElementById(this._syncContainerId);
    if (!syncDiv) {
      syncDiv = doc.createElement('div');
      syncDiv.setAttribute('id', this._syncContainerId);
      if (doc.body) {
        doc.body.appendChild(syncDiv);
      }
    }
    cleanFrame(doc, syncDiv, '');
    return syncDiv;
  }

  /**
   * Gets list of user sync requests and fires them after some delay
   *
   * @returns {undefined}
   */
  fireSync() {
    try {
      // empty and re-initialize container div
      const containerDiv = this._createAndCleanContainer();
      this._queue.forEach(sync => {
        const proc = this._processor[sync.type];
        proc.call(this, containerDiv, sync);
      });
    } catch (error) {
      logError(`Error firing user syncs: ${error}`);
    }

    this._queue.length = 0;
  }

  /**
   *
   * Fire image pixel for the given sync object
   *
   * @param {HTMLElement} containerDiv Sync Object Container div
   * @param {syncObj} sync Sync Object
   * @memberof _userSync
   * @returns {undefined}
   */
  _fireImagePixel(containerDiv: HTMLElement, sync: syncObj) {
    const { name, url } = sync;
    const img = new Image();
    img.src = url;
    containerDiv.appendChild(img);
    logDebug(`Invoking image pixel user sync for bidder: ${name}`);
  }

  /**
   *
   * Fire iframe usersync request
   *
   * @param {HTMLElement} containerDiv Sync Object Container div
   * @param {syncObj} sync Sync Object
   * @memberof _userSync
   * @returns {undefined}
   */
  _loadIframe(containerDiv: HTMLElement, sync: syncObj) {
    const { name, url } = sync;
    const iframeID = `${name}-sf-${getRandom()}`;
    const newIf = createIframe(window.document, iframeID, 0, 0, url);
    newIf.setAttribute('display', 'none');
    containerDiv.appendChild(newIf);
    logDebug(`Invoking iframe user sync for bidder: ${name}`);
  }
}

const us: _userSync = new _userSync();
export default us;
